import React from 'react';
import { motion } from 'framer-motion';
import {
  Zap,
  Twitter,
  Github,
  MessageCircle,
  Heart,
  ArrowUpRight,
} from 'lucide-react';

const productLinks = [
  { label: 'AI 写作', href: '#features' },
  { label: '文本润色', href: '#features' },
  { label: '多语言支持', href: '#features' },
  { label: 'API 接口', href: '#features' },
];

const resourceLinks = [
  { label: '使用文档', href: '#docs' },
  { label: '博客', href: '#blog' },
  { label: '更新日志', href: '#changelog' },
  { label: '社区', href: '#community' },
];

const legalLinks = [
  { label: '隐私政策', href: '#privacy' },
  { label: '服务条款', href: '#terms' },
  { label: 'Cookie 政策', href: '#cookies' },
];

const socialLinks = [
  { icon: Twitter, href: '#', label: 'Twitter' },
  { icon: Github, href: '#', label: 'GitHub' },
  { icon: MessageCircle, href: '#', label: 'Discord' },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

export function Footer() {
  return (
    <footer className="relative mt-auto">
      {/* Top gradient border */}
      <div className="absolute top-0 left-0 right-0 h-px">
        <div className="h-full bg-gradient-to-r from-transparent via-purple-500/50 to-transparent" />
        <div className="absolute inset-0 h-full bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent blur-sm" />
      </div>

      {/* Background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-purple-500/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 pb-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12"
        >
          {/* Brand Column */}
          <motion.div variants={itemVariants} className="sm:col-span-2 lg:col-span-1">
            <div className="glass-card p-6 h-full">
              <a href="#home" className="flex items-center gap-2 mb-4 group">
                <div className="relative flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-cyan-500">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <span className="text-lg font-bold gradient-text">HumanizeWriter</span>
              </a>
              <p className="text-sm text-white/50 leading-relaxed mb-6">
                让 AI 写作更有人味。智能润色、风格转换、多语言支持，打造真正自然的文字。
              </p>
              <div className="flex items-center gap-3">
                {socialLinks.map((social) => (
                  <motion.a
                    key={social.label}
                    href={social.href}
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center justify-center w-9 h-9 rounded-lg bg-white/5 border border-white/10 text-white/50 hover:text-purple-400 hover:border-purple-500/30 hover:bg-purple-500/10 hover:shadow-[0_0_15px_rgba(168,85,247,0.3)] transition-all"
                    aria-label={social.label}
                  >
                    <social.icon className="w-4 h-4" />
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Product Column */}
          <motion.div variants={itemVariants}>
            <div className="glass-card p-6 h-full">
              <h3 className="text-sm font-semibold text-white/90 uppercase tracking-wider mb-4">
                产品
              </h3>
              <ul className="space-y-3">
                {productLinks.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className="group flex items-center gap-1 text-sm text-white/50 hover:text-purple-400 transition-colors"
                    >
                      <span className="relative">
                        {link.label}
                        <span className="absolute bottom-0 left-0 w-0 h-px bg-purple-400 group-hover:w-full transition-all duration-300" />
                      </span>
                      <ArrowUpRight className="w-3 h-3 opacity-0 -translate-y-0.5 translate-x-0.5 group-hover:opacity-100 group-hover:translate-y-0 group-hover:translate-x-0 transition-all" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Resources Column */}
          <motion.div variants={itemVariants}>
            <div className="glass-card p-6 h-full">
              <h3 className="text-sm font-semibold text-white/90 uppercase tracking-wider mb-4">
                资源
              </h3>
              <ul className="space-y-3">
                {resourceLinks.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className="group flex items-center gap-1 text-sm text-white/50 hover:text-cyan-400 transition-colors"
                    >
                      <span className="relative">
                        {link.label}
                        <span className="absolute bottom-0 left-0 w-0 h-px bg-cyan-400 group-hover:w-full transition-all duration-300" />
                      </span>
                      <ArrowUpRight className="w-3 h-3 opacity-0 -translate-y-0.5 translate-x-0.5 group-hover:opacity-100 group-hover:translate-y-0 group-hover:translate-x-0 transition-all" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Legal Column */}
          <motion.div variants={itemVariants}>
            <div className="glass-card p-6 h-full">
              <h3 className="text-sm font-semibold text-white/90 uppercase tracking-wider mb-4">
                法律
              </h3>
              <ul className="space-y-3">
                {legalLinks.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className="group flex items-center gap-1 text-sm text-white/50 hover:text-pink-400 transition-colors"
                    >
                      <span className="relative">
                        {link.label}
                        <span className="absolute bottom-0 left-0 w-0 h-px bg-pink-400 group-hover:w-full transition-all duration-300" />
                      </span>
                      <ArrowUpRight className="w-3 h-3 opacity-0 -translate-y-0.5 translate-x-0.5 group-hover:opacity-100 group-hover:translate-y-0 group-hover:translate-x-0 transition-all" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </motion.div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="mt-12 pt-8 border-t border-white/5"
        >
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-white/40">
              © {new Date().getFullYear()} HumanizeWriter. All rights reserved.
            </p>
            <p className="flex items-center gap-1.5 text-sm text-white/40">
              Made with
              <motion.span
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="text-pink-500"
              >
                <Heart className="w-4 h-4 fill-current" />
              </motion.span>
              by the HumanizeWriter Team
            </p>
          </div>
        </motion.div>
      </div>
    </footer>
  );
}

export default Footer
